import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { Icon } from 'semantic-ui-react';

// Iframe media (item.media) sent by providers such as Black Widow. Shown after the images in the
// vehicle's gallery; clicking one opens it in a modal.
const MEDIA_TYPES = {
  undercarriage: { label: 'Undercarriage', icon: 'play circle outline' },
  interiorPano: { label: 'Interior 360°', icon: 'play circle outline' },
};

const describe = media =>
  MEDIA_TYPES[media.type] || { label: 'Video', icon: 'play circle outline' };

// The page is third party. It can run its own scripts (and talk to its own origin), but leaving out
// allow-top-navigation stops it from navigating our tab away.
const SANDBOX = 'allow-scripts allow-same-origin allow-popups';
const ALLOW = 'fullscreen; accelerometer; gyroscope';

const styles = {
  slide: {
    position: 'relative',
    width: '100%',
    aspectRatio: '4 / 3',
    backgroundColor: '#1b1c1d',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  slideImage: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    opacity: 0.6,
  },
  slideLabel: {
    position: 'relative',
    color: 'white',
    textAlign: 'center',
    fontSize: 22,
    // react-image-gallery sets line-height: 0 on its content, which stacks these lines on top of each other.
    lineHeight: 1.3,
    textShadow: '0 1px 3px rgba(0, 0, 0, .8)',
  },
  thumb: {
    position: 'relative',
    lineHeight: 1.1,
    width: '100%',
    aspectRatio: '4 / 3',
    backgroundColor: '#1b1c1d',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    overflow: 'hidden',
  },
  thumbImage: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  thumbBadge: {
    position: 'absolute',
    right: 2,
    bottom: 2,
    color: 'white',
    textShadow: '0 1px 2px rgba(0, 0, 0, .8)',
  },
  thumbLabel: {
    fontSize: 10,
    lineHeight: 1.1,
    marginTop: 2,
    textAlign: 'center',
  },
  modal: {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'white',
      zIndex: 99,
      position: 'absolute',
      borderRadius: '8px',
      border: 'none',
      padding: 12,
      width: 'min(1100px, 94vw)',
    },
    overlay: {
      zIndex: 99,
      backgroundColor: 'rgba(0, 0, 0, .6)',
    },
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  iframe: {
    display: 'block',
    width: '100%',
    height: 'min(700px, 75vh)',
    border: 'none',
  },
};

const https = url => (url ? url.replace(/^http:\/\//i, 'https://') : url);

const MediaSlide = ({ media, onOpen }) => {
  const { label, icon } = describe(media);
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open ${label}`}
      style={styles.slide}
      onClick={() => onOpen(media)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(media);
        }
      }}
    >
      {media.thumbnailUrl && (
        <img alt="" src={https(media.thumbnailUrl)} style={styles.slideImage} />
      )}
      <div style={styles.slideLabel}>
        <Icon name={icon} size="huge" style={{ display: 'block', margin: '0 auto 8px' }} />
        <div>{label}</div>
        <div style={{ fontSize: 14 }}>Click to view</div>
      </div>
    </div>
  );
};

const MediaThumb = ({ media }) => {
  const { label, icon } = describe(media);
  if (media.thumbnailUrl) {
    return (
      <div style={styles.thumb} title={label}>
        <img alt={label} src={https(media.thumbnailUrl)} style={styles.thumbImage} />
        <Icon name={icon} style={styles.thumbBadge} />
      </div>
    );
  }
  return (
    <div style={styles.thumb} title={label}>
      <Icon name={icon} size="large" style={{ margin: 0 }} />
      <div style={styles.thumbLabel}>{label}</div>
    </div>
  );
};

/** react-image-gallery items for an item's media, to append after its images. */
export const mediaGalleryItems = (media, onOpen) =>
  (media || [])
    .filter(m => m && m.url)
    .map(m => ({
      original: '',
      // The gallery leaves an item out of the thumbnail strip unless thumbnail is set. renderThumbInner
      // draws the thumb itself, so this value is only a placeholder.
      thumbnail: m.thumbnailUrl || `media:${m.type}`,
      thumbnailLabel: '',
      renderItem: () => <MediaSlide media={m} onOpen={onOpen} />,
      renderThumbInner: () => <MediaThumb media={m} />,
    }));

export const VehicleMediaModal = ({ media, onClose }) => {
  const label = media ? describe(media).label : '';
  return (
    <Modal
      isOpen={!!media}
      onRequestClose={onClose}
      style={styles.modal}
      contentLabel={label}
      ariaHideApp={false}
    >
      {media && (
        <>
          <div style={styles.modalHeader}>
            <h3 style={{ margin: 0 }}>{label}</h3>
            <Icon
              name="close"
              link
              size="large"
              aria-label="Close"
              onClick={onClose}
            />
          </div>
          <iframe
            key={media.url}
            title={label}
            src={https(media.url)}
            style={styles.iframe}
            sandbox={SANDBOX}
            allow={ALLOW}
            allowFullScreen
            referrerPolicy="no-referrer"
          />
        </>
      )}
    </Modal>
  );
};

const mediaShape = PropTypes.shape({
  type: PropTypes.string,
  url: PropTypes.string.isRequired,
  thumbnailUrl: PropTypes.string,
});

MediaSlide.propTypes = { media: mediaShape.isRequired, onOpen: PropTypes.func.isRequired };
MediaThumb.propTypes = { media: mediaShape.isRequired };
VehicleMediaModal.propTypes = { media: mediaShape, onClose: PropTypes.func.isRequired };
